import { Theme, alpha } from '@mui/material';
import type { FeedbackStatus, FeedbackType, FeedbackUrgency } from '@malash-office/types';

/**
 * Every colour פניות ומשוב uses, in one file — the same rule as `planningTones.ts` (ADR-022): the
 * screen is built from an approved Claude Design mockup whose colours belong to another palette, and
 * the translation lives where a reviewer can see all of it at once. No `.tsx` of this module holds a
 * hex literal.
 *
 * The mockup's accent was the wine `#660A23` (and `#F7EBEF` behind it). Ours is
 * `theme.palette.primary` — the requester asked for the colours to follow the system's, and for
 * nothing else to change.
 *
 * Two inks have no palette entry and are named here as literals, as `planningTones` does for its
 * own: the dark blue of "שיפור" / "חדש" and the brown of the duplicate notice. They come straight
 * from the mockup and are used nowhere else.
 */

export interface FeedbackTone {
  bg: string;
  ink: string;
}

const STEEL_INK = '#2E4A7A';
const BROWN_INK = '#8A4A10';

export const FEEDBACK_CARD_RADIUS = '20px';
export const FEEDBACK_INNER_RADIUS = '13px';
export const FEEDBACK_PILL_RADIUS = '50px';

/** The page ground, the cards, the grey wells inside them, and the hover of a row. */
export const feedbackSurfaces = (theme: Theme) => ({
  page: theme.palette.grey[400],
  card: theme.palette.common.white,
  well: theme.palette.grey[600],
  rowHover: alpha(theme.palette.grey[600], 0.6),
  rowLine: alpha(theme.palette.info.dark, 0.08),
  /** The frosted strip behind the two view tabs — the sidebar's glass. */
  glass: alpha(theme.palette.common.white, 0.42),
  glassShadow: `0 4px 50px ${alpha('#627083', 0.1)}, inset 0 0 10px ${alpha(theme.palette.common.white, 0.55)}`,
  overlay: alpha(theme.palette.info.dark, 0.35),
  toast: theme.palette.info.dark
});

export const feedbackTypeTones = (theme: Theme): Record<FeedbackType, FeedbackTone> => ({
  bug: { bg: alpha(theme.palette.error.main, 0.12), ink: theme.palette.error.main },
  feature: { bg: alpha(theme.palette.info.main, 0.16), ink: theme.palette.info.main },
  improvement: { bg: alpha(STEEL_INK, 0.1), ink: STEEL_INK },
  idea: { bg: alpha(theme.palette.secondary.main, 0.14), ink: BROWN_INK },
  question: { bg: alpha(theme.palette.success.main, 0.2), ink: theme.palette.primary.main }
});

export const feedbackStatusTones = (theme: Theme): Record<FeedbackStatus, FeedbackTone> => ({
  new: { bg: alpha(STEEL_INK, 0.1), ink: STEEL_INK },
  reviewing: { bg: alpha(theme.palette.secondary.main, 0.14), ink: BROWN_INK },
  in_progress: { bg: alpha(theme.palette.info.main, 0.16), ink: theme.palette.info.main },
  planned: { bg: theme.palette.grey[600], ink: theme.palette.text.primary },
  done: { bg: alpha(theme.palette.success.main, 0.2), ink: theme.palette.primary.main },
  rejected: { bg: alpha(theme.palette.error.main, 0.12), ink: theme.palette.error.main }
});

export const feedbackUrgencyDots = (theme: Theme): Record<FeedbackUrgency, string> => ({
  low: theme.palette.success.main,
  medium: theme.palette.secondary.main,
  high: theme.palette.error.main
});

/** "כפילות של FB-…", and the warm strip behind internal notes. */
export const feedbackNoticeTone = (theme: Theme): FeedbackTone => ({
  bg: alpha(theme.palette.secondary.main, 0.12),
  ink: BROWN_INK
});
